import { Box, Grid } from "@mui/material"
import { ChangeEvent, useCallback, useContext, useMemo } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { FormNumberInput } from "../../../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { FormSelectInput } from "../../../../../../../../../../../components/inputs/select-input/FormSelectInput"
import { BSMorphologyEnum, bsMorphologyLabelEnum } from "../../../../../../../../../../../core/enum/bsMorphologyEnum"
import { enumToSelectOptions } from "../../../../../../../../../../../core/services/helper-service"
import { BSLevelContext } from "../../../context/BSLevelContext"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"
import { GroupLevel } from "../../../context/GroupLevel"

interface IProps {
  groupLevel: GroupLevel
  isRectangleShape: boolean
  isLShape: boolean
}

export function EditableLevelMorphology({ groupLevel, isRectangleShape, isLShape }: Readonly<IProps>): React.JSX.Element {
  const { setFormFieldValue, errors, generateUniqueNestedErrorKey, validateNonZero } = useContext(BSLevelFormContext)
  const { setIsLevelUpdated } = useContext(BSLevelContext)

  const isSuperstructure = useMemo(() => groupLevel.levelList[0].superstructure, [groupLevel.levelList])

  const dimensionRectangle1 = groupLevel.levelList[0].rectangleDimension?.dimensionRectangle1
  const dimensionRectangle2 = groupLevel.levelList[0].rectangleDimension?.dimensionRectangle2

  const dimensionL1 = groupLevel.levelList[0].shapeLDimension?.dimensionL1
  const dimensionL2 = groupLevel.levelList[0].shapeLDimension?.dimensionL2
  const dimensionL3 = groupLevel.levelList[0].shapeLDimension?.dimensionL3
  const dimensionL4 = groupLevel.levelList[0].shapeLDimension?.dimensionL4

  const handleChangeShape: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
    (event) => {
      const selectedShape = event.target.value as BSMorphologyEnum

      if (selectedShape === BSMorphologyEnum.RECTANGLE) {
        setFormFieldValue(
          "rectangleDimension",
          { dimensionRectangle1: 0, dimensionRectangle2: 0 },
          groupLevel.id,
          isSuperstructure
        )
        setFormFieldValue("shapeLDimension", undefined, groupLevel.id, isSuperstructure)
      } else if (selectedShape === BSMorphologyEnum.L) {
        setFormFieldValue(
          "shapeLDimension",
          { dimensionL1: 0, dimensionL2: 0, dimensionL3: 0, dimensionL4: 0 },
          groupLevel.id,
          isSuperstructure
        )
        setFormFieldValue("rectangleDimension", undefined, groupLevel.id, isSuperstructure)
      }

      setIsLevelUpdated(true)
    },
    [groupLevel.id, isSuperstructure, setFormFieldValue, setIsLevelUpdated]
  )

  const handleChangeDimension: (fieldName: string, value: number) => void = useCallback(
    (fieldName, value) => {
      if (isRectangleShape) {
        const prev = groupLevel.levelList[0].rectangleDimension ?? {
          dimensionRectangle1: 0,
          dimensionRectangle2: 0,
        }
        validateNonZero(groupLevel.id, "rectangleDimension", fieldName, value)
        setFormFieldValue("rectangleDimension", { ...prev, [fieldName]: value }, groupLevel.id, isSuperstructure)
      }

      if (isLShape) {
        const prev = groupLevel.levelList[0].shapeLDimension ?? {
          dimensionL1: 0,
          dimensionL2: 0,
          dimensionL3: 0,
          dimensionL4: 0,
        }
        validateNonZero(groupLevel.id, "shapeLDimension", fieldName, value)
        setFormFieldValue("shapeLDimension", { ...prev, [fieldName]: value }, groupLevel.id, isSuperstructure)
      }

      setIsLevelUpdated(true)
    },
    [
      groupLevel.id,
      groupLevel.levelList,
      isLShape,
      isRectangleShape,
      isSuperstructure,
      setFormFieldValue,
      setIsLevelUpdated,
      validateNonZero,
    ]
  )

  const errorKeyDimensionRectangle1 = generateUniqueNestedErrorKey(
    groupLevel.id,
    "rectangleDimension",
    "dimensionRectangle1"
  )
  const errorKeyDimensionRectangle2 = generateUniqueNestedErrorKey(
    groupLevel.id,
    "rectangleDimension",
    "dimensionRectangle2"
  )
  const errorKeyDimensionL1 = generateUniqueNestedErrorKey(groupLevel.id, "shapeLDimension", "dimensionL1")
  const errorKeyDimensionL2 = generateUniqueNestedErrorKey(groupLevel.id, "shapeLDimension", "dimensionL2")
  const errorKeyDimensionL3 = generateUniqueNestedErrorKey(groupLevel.id, "shapeLDimension", "dimensionL3")
  const errorKeyDimensionL4 = generateUniqueNestedErrorKey(groupLevel.id, "shapeLDimension", "dimensionL4")

  return (
    <Grid item xs={6}>
      <FormSelectInput
        id="shape"
        label="Forme du bâtiment"
        selectedOption={isRectangleShape ? BSMorphologyEnum.RECTANGLE : BSMorphologyEnum.L}
        options={enumToSelectOptions(BSMorphologyEnum, bsMorphologyLabelEnum)}
        handleEventChange={(event) => handleChangeShape(event)}
        mode="event"
        formBackgroundColor={customPalette.lightGray6}
      />

      {isRectangleShape && (
        <Box display="flex" gap={2}>
          <FormNumberInput
            fieldName="dimensionRectangle1"
            label="Dimension 1 (m)"
            handleChange={(value) => handleChangeDimension("dimensionRectangle1", Number(value))}
            value={dimensionRectangle1}
            errors={
              errors[errorKeyDimensionRectangle1] ? { dimensionRectangle1: errors[errorKeyDimensionRectangle1] } : undefined
            }
            formBackgroundColor={customPalette.lightGray6}
          />
          <FormNumberInput
            fieldName="dimensionRectangle2"
            label="Dimension 2 (m)"
            handleChange={(value) => handleChangeDimension("dimensionRectangle2", Number(value))}
            value={dimensionRectangle2}
            errors={
              errors[errorKeyDimensionRectangle2] ? { dimensionRectangle2: errors[errorKeyDimensionRectangle2] } : undefined
            }
            formBackgroundColor={customPalette.lightGray6}
          />
        </Box>
      )}

      {isLShape && (
        <>
          <Box display="flex" gap={2}>
            <FormNumberInput
              fieldName="dimensionL1"
              label="Dimension 1 (m)"
              handleChange={(value) => handleChangeDimension("dimensionL1", Number(value))}
              value={dimensionL1}
              errors={errors[errorKeyDimensionL1] ? { dimensionL1: errors[errorKeyDimensionL1] } : undefined}
              formBackgroundColor={customPalette.lightGray6}
            />
            <FormNumberInput
              fieldName="dimensionL2"
              label="Dimension 2 (m)"
              handleChange={(value) => handleChangeDimension("dimensionL2", Number(value))}
              value={dimensionL2}
              errors={errors[errorKeyDimensionL2] ? { dimensionL2: errors[errorKeyDimensionL2] } : undefined}
              formBackgroundColor={customPalette.lightGray6}
            />
          </Box>
          <Box display="flex" gap={2}>
            <FormNumberInput
              fieldName="dimensionL3"
              label="Dimension 3 (m)"
              handleChange={(value) => handleChangeDimension("dimensionL3", Number(value))}
              value={dimensionL3}
              errors={errors[errorKeyDimensionL3] ? { dimensionL3: errors[errorKeyDimensionL3] } : undefined}
              formBackgroundColor={customPalette.lightGray6}
            />
            <FormNumberInput
              fieldName="dimensionL4"
              label="Dimension 4 (m)"
              handleChange={(value) => handleChangeDimension("dimensionL4", Number(value))}
              value={dimensionL4}
              errors={errors[errorKeyDimensionL4] ? { dimensionL4: errors[errorKeyDimensionL4] } : undefined}
              formBackgroundColor={customPalette.lightGray6}
            />
          </Box>
        </>
      )}
    </Grid>
  )
}
